import { useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SmartTable } from "@/components/SmartTable";
import { api, errMsg, money, moisLabel } from "@/lib/api";

const COULEURS = ["#047857", "#0F172A", "#D97706", "#2563EB", "#DC2626"];
const axe = { fontSize: 11, fill: "#64748b" };
const num = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".").replace(/\s/g, "")));

export const SoldeCourbe = ({ annee, courbe }) => {
  if (!courbe?.series.length) { return null; }
  const par = Object.fromEntries(courbe.mois.map((m) => [m.mois, m]));
  const data = Array.from({ length: 12 }, (_, i) => { const m = `${annee}-${String(i + 1).padStart(2, "0")}`; return { ...par[m], mois: m, label: moisLabel(m) }; });
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-slate-900">Solde restant des prêts</h3>
      <div className="h-72" data-testid="hypotheque-courbe">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={axe} />
            <YAxis tick={axe} tickFormatter={(v) => `${(v / 1000).toLocaleString("fr-CA", { maximumFractionDigits: 1 })} k$`} width={60} domain={["auto", "auto"]} />
            <Tooltip formatter={(v) => money(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {courbe.series.map((s, i) => <Line key={s} type="monotone" dataKey={s} name={s} stroke={COULEURS[i % COULEURS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const Champ = ({ label, children }) => <label className="space-y-1 text-xs text-slate-600"><span className="block">{label}</span>{children}</label>;

const vide = (mois) => ({ mois, interets: "", paiement: "2500", solde: "" });
const depuis = (l) => ({ mois: l.mois, interets: String(l.interets), paiement: String(l.paiement), solde: l.solde == null ? "" : String(l.solde) });

const MargeForm = ({ f, setF, onDone }) => {
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = /^\d{4}-\d{2}$/.test(f.mois) && num(f.interets) != null && num(f.paiement) != null;
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/hypotheque/marge", { mois: f.mois, interets: num(f.interets), paiement: num(f.paiement), solde: num(f.solde) });
      toast.success(`Marge de crédit : ${moisLabel(f.mois)} enregistré`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      <Champ label="Mois"><Input type="month" value={f.mois} onChange={(e) => setF(e.target.value, true)} className="h-9 w-40 bg-white" data-testid="marge-mois-input" /></Champ>
      <Champ label="Intérêts ($)"><Input inputMode="decimal" value={f.interets} onChange={set("interets")} className="h-9 w-32 bg-white" data-testid="marge-interets-input" /></Champ>
      <Champ label="Paiement total ($)"><Input inputMode="decimal" value={f.paiement} onChange={set("paiement")} className="h-9 w-32 bg-white" data-testid="marge-paiement-input" /></Champ>
      <Champ label="Solde fin de mois (facultatif)"><Input inputMode="decimal" value={f.solde} onChange={set("solde")} className="h-9 w-44 bg-white" data-testid="marge-solde-input" /></Champ>
      <Button onClick={save} disabled={!ok || busy} className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="marge-enregistrer">
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Enregistrer
      </Button>
    </div>
  );
};

const MargeListe = ({ lignes, onDone, onModifier }) => {
  const suppr = async (mois) => {
    if (!window.confirm(`Supprimer la saisie de ${moisLabel(mois)} ?`)) { return; }
    try { await api.delete(`/hypotheque/marge/${mois}`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!lignes.length) { return null; }
  return (
    <div className="overflow-x-auto">
      <SmartTable id="hypotheque-marge" data-export-titre="Marge de crédit saisie" className="w-full min-w-[640px] text-sm" data-testid="marge-table">
        <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500"><th className="px-3 py-2 text-left">Mois</th><th className="px-3 py-2 text-right">Paiement</th><th className="px-3 py-2 text-right">Intérêts</th><th className="px-3 py-2 text-right">Capital</th><th className="px-3 py-2 text-right">Solde</th><th data-st-export="non" /></tr></thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.mois} className="border-t border-slate-100" data-testid={`marge-ligne-${l.mois}`}>
              <td className="px-3 py-2.5"><button type="button" onClick={() => onModifier(l)} className="text-emerald-700 hover:underline" title="Modifier cette saisie" data-testid={`marge-modifier-${l.mois}`}>{moisLabel(l.mois)}</button></td><td className="px-3 py-2.5 text-right tabular">{money(l.paiement)}</td>
              <td className="px-3 py-2.5 text-right tabular font-medium text-emerald-700">{money(l.interets)}</td><td className="px-3 py-2.5 text-right tabular">{money(l.capital)}</td>
              <td className="px-3 py-2.5 text-right tabular text-slate-600">{l.solde == null ? "—" : money(l.solde)}</td>
              <td className="px-3 py-2.5 text-right"><button type="button" onClick={() => suppr(l.mois)} className="text-slate-400 hover:text-red-600" data-testid={`marge-supprimer-${l.mois}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
            </tr>
          ))}
        </tbody>
      </SmartTable>
    </div>
  );
};

export const MargeSaisie = ({ annee, lignes, onDone }) => {
  const [f, setFState] = useState(vide(`${annee}-01`));
  const setF = (v, mois = false) => {
    if (!mois) { setFState(v); return; }
    const l = lignes.find((x) => x.mois === v);
    setFState(l ? depuis(l) : { ...vide(v), paiement: f.paiement });
  };
  return (
    <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid="marge-saisie">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Marge de crédit : saisie mensuelle</h3>
        <p className="text-xs text-slate-500">Quand le relevé ne détaille pas les intérêts : entrez les intérêts et le paiement total du mois (capital = paiement − intérêts). Choisir un mois déjà saisi (ou cliquer sur le mois dans le tableau) reprend ses valeurs pour les modifier. Choisissez ensuite l'immeuble de « Marge de crédit (saisie) » ci-dessus.</p>
      </div>
      <MargeForm f={f} setF={setF} onDone={onDone} />
      <MargeListe lignes={lignes} onDone={onDone} onModifier={(l) => setFState(depuis(l))} />
    </div>
  );
};
